import { Table, CoursesIcon, Button, Row, TableColumns } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { CollaborationStatus } from 'client/gql/graphql';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { Collaboration } from 'pages/Settings/subpages/SettingsCollaborationsSubpage';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const CollaborationsTableFieldsDocument = graphql(`
  fragment CollaborationsTableFields on Query {
    collaboratingOrganizations(uuid: $organizationUuid) {
      uuid
      __typename
      name
      collaborationStatus
      sourceOrganization
    }
    ...PermissionCheckerFields
  }
`);

type CollaborationsTableProps = {
  data: FragmentType<typeof CollaborationsTableFieldsDocument> | undefined;
  onAdd: () => void;
  onAccept: (uuid: string) => void;
  onCancel: (uuid: string) => void;
};

export const CollaborationsTable = ({ data, onAdd, onAccept, onCancel }: CollaborationsTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const fragment = getFragmentData(CollaborationsTableFieldsDocument, data);

  const permissions = usePermissionChecker(fragment);

  const columns: TableColumns<Collaboration>[] = useMemo(() => {
    return [
      {
        header: t('common.name'),
        id: 'name',
        accessorKey: 'name',
        size: 200,
        canExpand: true,
      },
      {
        id: 'collaborationStatus',
        header: t('common.status'),
        cell: ({ row }: { row: Row<Collaboration> }) => {
          if (row.original.collaborationStatus === CollaborationStatus.Pending) return t('common.requested');
          if (row.original.collaborationStatus === CollaborationStatus.CollaborationCancelled)
            return t('collaborations.collaborationCancelled');
          if (row.original.collaborationStatus === CollaborationStatus.Collaboration)
            return t('collaborations.titleSingular');
          if (row.original.collaborationStatus === CollaborationStatus.NoCollaboration)
            return t('collaborations.notCollaboration');
          if (row.original.collaborationStatus === CollaborationStatus.NoCollaborationPossible)
            return t('collaborations.notPossible');
        },
        size: 150,
      },
    ];
  }, [t]);

  const tableData = useMemo(() => {
    return (
      (fragment?.collaboratingOrganizations as Collaboration[]).filter((collab) => {
        const filteredStates = [CollaborationStatus.NoCollaborationPossible, CollaborationStatus.NoCollaboration];
        return !filteredStates.includes(collab.collaborationStatus);
      }) ?? []
    );
  }, [fragment?.collaboratingOrganizations]);

  return (
    <Table<Collaboration>
      columns={columns}
      data={tableData}
      sorting={[{ id: 'name', desc: false }]}
      showActionBar
      breakpoint='580px'
      isOnWhite={false}
      showBorderRadius
      showShadow
      actionBarSettings={{
        showAddButton: permissions?.canCreateCollaboration({ uuid: organizationUuid }),
        addButtonText: t('collaborations.addCollaboration'),
        showExpertFilter: true,
      }}
      onAddClick={onAdd}
      emptyStateSettings={{ icon: <CoursesIcon className='svg-icon primary-light' /> }}
      lastColWidth='175px'
      lastCol={(row) => {
        switch (row.original.collaborationStatus) {
          case CollaborationStatus.Collaboration:
            return (
              <Button hierarchy={'secondary'} onClick={() => alert(t('collaborations.cancelCollaborationNotPossible'))}>
                {t('collaborations.cancelCollaboration')}
              </Button>
            );
          case CollaborationStatus.Pending:
            if (row.original.sourceOrganization !== organizationUuid) {
              return (
                <Button hierarchy={'secondary'} onClick={() => onAccept(row.original.uuid)}>
                  {t('collaborations.ackCollaboration')}
                </Button>
              );
            } else {
              return (
                <Button hierarchy={'secondary'} onClick={() => onCancel(row.original.uuid)}>
                  {t('collaborations.abortCollaboration')}
                </Button>
              );
            }
          default:
            return <></>;
        }
      }}
    />
  );
};
